<template>
  <q-btn-dropdown
    v-bind="dropdownProps"
  >
    <template #label>
      <span>
        <q-icon
          v-if="useLinkStyle || hideLabel"
          name="add"
          :class="{ 'icon-margin': useLinkStyle }"
        />
        <span v-if="!hideLabel">
          <slot name="label">
            {{ $t('programs.buttonAddProgram') }}
          </slot>
        </span>
      </span>
    </template>
    <q-list
      data-e2e="list-templates-programs"
    >
      <q-input
        v-if="!hideSearch"
        v-model="searchQuery"
        debounce="500"
        :label="$t('programs.inputSearch')"
        dense
        autofocus
        hide-bottom-space
        outlined
        clearable
        @update:model-value="onSearchInput"
      >
        <template
          v-if="isSearchInProgress"
          #append
        >
          <q-spinner />
        </template>
      </q-input>
      <template v-if="isSearching">
        <q-item
          dense
          class="items-center justify-center bg-primary text-white text-bold"
        >
          <q-item-section>
            <q-item-label color="dark">
              {{ $t('programs.headingSearchResults') }}
            </q-item-label>
          </q-item-section>
        </q-item>
        <template v-if="!hasSearchResults && !isSearchInProgress">
          <q-item
            class="items-center"
          >
            <q-item-section>
              <q-item-label color="dark">
                {{ $t('programs.textNoSearchResults') }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
        <template v-else>
          <q-item
            v-for="searchResult in searchResults"
            :key="searchResult.id"
            v-ripple
            clickable
            class="items-center"
          >
            <q-item-section
              v-close-popup
              @click="onUseSearchResultProgram(searchResult.id)"
            >
              <q-item-label color="dark">
                {{ searchResult.programName }}
              </q-item-label>
            </q-item-section>
            <q-item-section
              avatar
            >
              <q-btn
                flat
                round
                dense
                color="primary"
                icon="preview"
                @click="onPreviewSearchResult(searchResult)"
              >
                <q-tooltip>
                  {{ $t('programs.listItemPreview') }}
                </q-tooltip>
              </q-btn>
            </q-item-section>
          </q-item>
        </template>
      </template>
      <template v-else>
        <template v-if="showTemplates">
          <q-item
            v-if="isLoadingProgramTemplates"
            dense
            class="justify-center items-center"
          >
            <q-spinner />
          </q-item>
          <template v-else>
            <q-item
              dense
              class="items-center justify-center bg-primary text-white text-bold"
            >
              <q-item-section>
                <q-item-label color="dark">
                  {{ $t('programs.headingTemplate') }}
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item
              v-for="programTemplate in sortedProgramTemplates"
              :key="programTemplate.id"
              v-ripple
              clickable
              class="items-center"
            >
              <q-item-section
                v-close-popup
                @click="onUseTemplateProgram(programTemplate.id)"
              >
                <q-item-label color="dark">
                  {{ programTemplate.programName }}
                </q-item-label>
              </q-item-section>
              <q-item-section
                avatar
              >
                <q-btn
                  flat
                  round
                  dense
                  color="primary"
                  icon="preview"
                  @click="onPreviewProgram(programTemplate)"
                >
                  <q-tooltip>
                    {{ $t('programs.listItemPreview') }}
                  </q-tooltip>
                </q-btn>
              </q-item-section>
            </q-item>
          </template>
        </template>
        <q-separator />
        <template v-if="hasClientPrograms">
          <q-item
            dense
            class="items-center justify-center bg-primary text-white text-bold"
          >
            <q-item-section>
              <q-item-label color="dark">
                {{ $t('programs.headingBlankClientProgram') }}
              </q-item-label>
            </q-item-section>
          </q-item>

          <q-item
            v-close-popup
            clickable
            data-e2e="list-item-template-program-skill-acquisition"
            @click="onAddProgram('simple', 'client')"
          >
            <q-item-section>
              <q-item-label>
                {{ $t('programType_simple') }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-close-popup
            clickable
            data-e2e="list-item-template-program-frequency"
            @click="onAddProgram('frequency', 'client')"
          >
            <q-item-section>
              <q-item-label>
                {{ $t('programType_frequency') }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-close-popup
            clickable
            data-e2e="list-item-template-program-interval"
            @click="onAddProgram('interval', 'client')"
          >
            <q-item-section>
              <q-item-label>
                {{ $t('programType_interval') }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-close-popup
            clickable
            data-e2e="list-item-template-program-duration"
            @click="onAddProgram('duration', 'client')"
          >
            <q-item-section>
              <q-item-label>
                {{ $t('programType_duration') }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-close-popup
            clickable
            data-e2e="list-item-template-program-task-analysis"
            @click="onAddProgram('taskAnalysis', 'client')"
          >
            <q-item-section>
              <q-item-label>
                {{ $t('programType_taskAnalysis') }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            v-if="behaviorTrackingEnabled"
            v-close-popup
            clickable
            data-e2e="list-item-template-client-program-behavior-tracking"
            @click="onAddProgram('behaviorTracking', 'client')"
          >
            <q-item-section>
              <q-item-label>
                {{ $t('programType_behaviorTracking') }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-expansion-item
            :label="$t('programs.textExpandMore')"
          >
            <q-list>
              <q-item
                v-close-popup
                clickable
                :inset-level="0.5"
                @click="onAddProgram('esdm', 'client')"
              >
                <q-item-section>
                  <q-item-label>
                    {{ $t('programType_esdm') }}
                  </q-item-label>
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :inset-level="0.5"
                @click="onAddProgram('set', 'client')"
              >
                <q-item-section>
                  <q-item-label>
                    {{ $t('programType_set') }}
                  </q-item-label>
                </q-item-section>
              </q-item>
              <q-item
                v-if="userIsAdmin"
                clickable
                :inset-level="0.5"
                @click="onAddProgram('ptp', 'client')"
              >
                <q-item-section>
                  <q-item-label>
                    {{ $t('programType_ptp') }}
                  </q-item-label>
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :inset-level="0.5"
                @click="onAddProgram('instructions', 'client')"
              >
                <q-item-section>
                  <q-item-label>
                    {{ $t('programType_instructions') }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-expansion-item>
          <q-separator />
          <q-item
            v-close-popup
            clickable
            :disable="disableBehaviorProgram"
            @click="onAddProgram('behaviour', 'client')"
          >
            <q-item-section>
              <q-item-label>
                {{ $t('programType_behaviour') }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-separator />
        </template>
      </template>
      <template v-if="hasStaffPrograms">
        <q-item
          dense
          class="items-center justify-center bg-primary text-white text-bold"
        >
          <q-item-section>
            <q-item-label color="dark">
              {{ $t('programs.headingBlankStaffProgram') }}
            </q-item-label>
          </q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          data-e2e="list-item-template-program-skill-acquisition"
          @click="onAddProgram('simple', 'staff')"
        >
          <q-item-section>
            <q-item-label>
              {{ $t('programType_simple') }}
            </q-item-label>
          </q-item-section>
        </q-item>
        <q-item
          v-close-popup
          clickable
          data-e2e="list-item-template-program-interval"
          @click="onAddProgram('interval', 'staff')"
        >
          <q-item-section>
            <q-item-label>
              {{ $t('programType_interval') }}
            </q-item-label>
          </q-item-section>
        </q-item>
        <q-item
          v-close-popup
          clickable
          data-e2e="list-item-template-program-instructions"
          @click="onAddProgram('instructions', 'staff')"
        >
          <q-item-section>
            <q-item-label>
              {{ $t('programType_instructions') }}
            </q-item-label>
          </q-item-section>
        </q-item>
        <q-item
          v-close-popup
          clickable
          data-e2e="list-item-template-program-active-student-response"
          @click="onAddProgram('asr', 'staff')"
        >
          <q-item-section>
            <q-item-label>
              {{ $t('programType_asr') }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-list>
  </q-btn-dropdown>
</template>
<script>
import ProgramInfo from 'components/Programs/ProgramInfo';
import { isEmpty, trim } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { callFirebaseFunction } from 'src/boot/firebase';
import { useBehaviorTrackingFeature } from 'src/composables/analytics/useBehaviorTrackingFeature';
import ProgramsDB from 'src/fb/programs-db';
import Interactions from 'src/mixins/Interactions';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';

export default {
  name: 'AddProgramButton',

  mixins: [Interactions, Toolbox],

  props: {
    disableBehaviorProgram: {
      type: Boolean,
      default: false,
    },

    hideLabel: {
      type: Boolean,
      default: false,
    },

    hideSearch: {
      type: Boolean,
      default: false,
    },

    hideTemplates: {
      type: Boolean,
      default: false,
    },

    useLinkStyle: {
      type: Boolean,
      default: false,
    },

    programClientTypes: {
      type: Array,
      default: () => ['client', 'staff'],
    },
  },

  emits: ['add-program', 'use-program'],

  setup() {
    const { behaviorTrackingEnabled } = useBehaviorTrackingFeature();

    return { behaviorTrackingEnabled };
  },

  data() {
    return {
      searchQuery: '',
      isSearchInProgress: false,
      searchResults: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'userIsAdmin']),
    ...mapState(programs, ['programTemplates', 'isLoadingProgramTemplates']),
    ...mapState(network, ['offlineMode']),

    dropdownProps() {
      return {
        'menu-self': this.useLinkStyle ? 'top left' : 'top end',
        unelevated: this.useLinkStyle,
        square: this.useLinkStyle,
        align: this.useLinkStyle ? 'between' : 'center',
        'no-caps': this.useLinkStyle,
        disable: this.offlineMode,
        color: this.useLinkStyle ? undefined : 'primary',
        class: { 'q-btn-hide-dropdown-arrow': this.useLinkStyle },
      };
    },

    isSearching() {
      return ((trim(this.searchQuery) || '') !== '');
    },

    hasSearchResults() {
      return !isEmpty(this.searchResults);
    },

    hasClientPrograms() {
      return this.programClientTypes.includes('client');
    },

    hasStaffPrograms() {
      return this.programClientTypes.includes('staff');
    },

    clientTypeForSearchEngine() {
      if (this.hasClientPrograms && this.hasStaffPrograms) {
        return undefined;
      }
      return this.hasClientPrograms ? 'client' : 'staff';
    },

    showTemplates() {
      return !this.hideTemplates && (this.isLoadingProgramTemplates || !isEmpty(this.sortedProgramTemplates));
    },

    sortedProgramTemplates() {
      return this.naturalSort(
        this.programTemplates.filter((program) => (this.programClientTypes.includes(program.clientType ?? 'client'))),
        'programName',
      );
    },
  },

  created() {
    if (!this.offlineMode) {
      this.bindProgramTemplates();
    }
  },

  methods: {
    ...mapActions(programs, ['bindProgramTemplates']),

    onAddProgram(programType, clientType) {
      this.$emit('add-program', programType, clientType);
    },

    onUseSearchResultProgram(programId) {
      this.$emit('use-program', programId, false);
    },

    onUseTemplateProgram(programId) {
      this.$emit('use-program', programId, true);
    },

    openProgramPreview(program) {
      this.openComponentDialog(ProgramInfo, { program });
    },

    async onPreviewSearchResult(searchResult) {
      this.$q.loading.show();
      const programsDb = new ProgramsDB();
      try {
        const program = await programsDb.read(searchResult.id);
        this.openProgramPreview(program);
      } catch (err) {
        this.notifyNegative(this.$t('programs.notifyPreviewNotFound'));
      } finally {
        this.$q.loading.hide();
      }
    }, // onPreviewProgram

    onPreviewProgram(program) {
      this.openProgramPreview(program);
    }, // onPreviewProgram

    onSearchInput() {
      this.searchPrograms();
    },

    async searchPrograms() {
      if (this.searchQuery) {
        this.isSearchInProgress = true;

        const { data } = await callFirebaseFunction('findLibraryPrograms', {
          query: this.searchQuery,
          clinic: this.userDetails.clinic,
          clientType: this.clientTypeForSearchEngine,
        });

        this.isSearchInProgress = false;
        this.searchResults = data;
      }
    },
  },

};
</script>

<style>
.q-btn-hide-dropdown-arrow .q-btn-dropdown__arrow {
  display: none;
}
.icon-margin {
  margin-right: 32px;
}
</style>
